import type { ReportCell } from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';

/**
 * Texto e geometria das celulas dos relatorios.
 *
 * O servidor manda o valor inteiro (centavos, pontos-base, quantidade) e a tela so o
 * escreve. A medida sem definicao chega como "pendente" e sai escrita assim: um zero
 * no lugar dela seria um numero que ninguem calculou.
 */

export const TEXTO_PENDENTE = 'Pendente de definicao';

/** Texto da celula, com a formatacao do tipo. */
export function textoDaCelula(celula: ReportCell | undefined): string {
  if (!celula) return '';
  if (celula.status === 'pendente') return TEXTO_PENDENTE;
  if (celula.status === 'vazio') return '';
  switch (celula.kind) {
    case 'dinheiro':
      return formatMoney(celula.money);
    case 'pontos_base':
      return formatRate(celula.number ?? 0);
    case 'quantidade':
      return (celula.number ?? 0).toLocaleString('pt-BR');
    case 'dias':
      return `${(celula.number ?? 0).toLocaleString('pt-BR')} ${celula.number === 1 ? 'dia' : 'dias'}`;
    case 'data':
      return formatCivilDate(celula.date);
    case 'data_hora':
      return formatDateTime(celula.datetime);
    case 'booleano':
      return celula.boolean ? 'Sim' : 'Nao';
    case 'duracao':
      return duracaoDDHHMMSS(celula.number ?? 0);
    default:
      return celula.text ?? '';
  }
}

function doisDigitos(valor: number): string {
  return String(valor).padStart(2, '0');
}

/**
 * Duracao em segundos no formato DD HH:MM:SS do OnPoint, com dias de 24 horas: e a
 * duracao corrida da passagem pela esteira (a util, de 9 horas, esta pendente).
 */
export function duracaoDDHHMMSS(segundos: number): string {
  const total = Math.max(0, Math.trunc(segundos));
  const dias = Math.trunc(total / 86400);
  const resto = total % 86400;
  const horas = Math.trunc(resto / 3600);
  const minutos = Math.trunc((resto % 3600) / 60);
  return `${doisDigitos(dias)} ${doisDigitos(horas)}:${doisDigitos(minutos)}:${doisDigitos(resto % 60)}`;
}

/**
 * Grandeza da celula para o tamanho da marca no grafico. Nao e valor exibido: o
 * texto continua vindo de `textoDaCelula`, sobre o inteiro do servidor.
 */
export function grandezaDaCelula(celula: ReportCell | undefined): number | null {
  if (!celula || celula.status !== 'ok') return null;
  if (celula.kind === 'dinheiro') return celula.money?.amount ?? null;
  if (typeof celula.number === 'number') return celula.number;
  return null;
}

/** Celula alinhada a direita: numero, dinheiro, percentual, data. */
export function alinhaADireita(kind: string): boolean {
  return [
    'dinheiro',
    'pontos_base',
    'quantidade',
    'dias',
    'data',
    'data_hora',
    'duracao',
  ].includes(kind);
}

/** Classes da paleta categorica, na ordem fixa: a serie N tem sempre a cor N. */
export const coresDaSerie = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
  'bg-chart-6',
  'bg-chart-7',
  'bg-chart-8',
] as const;

/** Cor da serie pela posicao; da nona em diante, cinza ("outras"). */
export function corDaSerie(posicao: number): string {
  return coresDaSerie[posicao] ?? 'bg-ink-400';
}
